import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Button,
  Card,
  Col,
  Drawer,
  Radio,
  Row,
  Skeleton,
  Space,
  Table,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd';
import {
  AimOutlined,
  BookOutlined,
  CheckCircleOutlined,
  ExperimentOutlined,
  EyeOutlined,
  InfoCircleOutlined,
  SafetyCertificateOutlined,
  TeamOutlined,
  WarningOutlined,
} from '@ant-design/icons';
import { useResponsive } from '../contexts/ResponsiveContext';
import { intakeAPI } from '../services/api';
import type {
  BoundaryMatchDetail,
  Covariates,
  IntakeContextResponse,
  AdministrationMode,
  ModelMatchResult,
} from '../types';
import { getCanonicalModelDisplayName } from '../utils/modelDisplay';
import { getPopulationTagColor } from '../utils/clinicalTagPalette';
import { formatErrorMessage } from '../utils/errorHandler';
import { getAdministrationModeLabel } from '../utils/doseMethodVisibility';

const { Paragraph, Text } = Typography;

interface ModelRecommendationProps {
  covariates: Covariates;
  drugName?: string;
  onSelect: (model: ModelMatchResult) => void;
  onFallback: () => void;
  supportsPkFormula?: boolean;
  administrationMode: AdministrationMode;
  supportedAdministrationModes: AdministrationMode[];
  onAdministrationModeChange: (mode: AdministrationMode) => void;
}

type ModelTier = 'primary' | 'secondary';

const STYLES = `
  .model-confirmation {
    display: flex;
    flex-direction: column;
    gap: 16px;
    color: #102A43;
  }

  .model-confirmation__panel {
    overflow: hidden;
    border: 1px solid #DDE7F1;
    border-radius: 18px;
    background: #FFFFFF;
    box-shadow: 0 10px 28px rgba(15, 39, 66, 0.045);
  }

  .model-confirmation__panel-head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 16px;
    padding: 20px 20px 0;
  }

  .model-confirmation__panel-title {
    color: #0A2239;
    font-size: 20px;
    font-weight: 750;
    line-height: 1.25;
    text-wrap: balance;
  }

  .model-confirmation__panel-desc {
    max-width: 720px;
    margin-top: 5px;
    color: #62778C;
    font-size: 13px;
    line-height: 1.55;
  }

  .model-confirmation__panel-actions {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    gap: 8px;
  }

  .model-confirmation__panel-body { padding: 16px 20px 20px; }
  .model-confirmation__mode-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    margin-bottom: 16px;
    border-bottom: 1px solid #E7EDF4;
    padding-bottom: 14px;
  }
  .model-confirmation__mode-label { color: #334E68; font-size: 13px; font-weight: 700; }
  .model-confirmation__card-grid.ant-row { align-items: stretch; }
  .model-confirmation__card-grid.ant-row > .ant-col { display: flex; }
  .model-confirmation__card-grid.ant-row > .ant-col > .model-confirmation__model-card { flex: 1 1 auto; }

  .model-confirmation__swipe-rail {
    display: flex;
    gap: 12px;
    overflow-x: auto;
    padding: 2px 2px 8px;
    scroll-padding-inline: 2px;
    scroll-snap-type: x mandatory;
    overscroll-behavior-x: contain;
    -webkit-overflow-scrolling: touch;
  }

  .model-confirmation__swipe-rail.ant-row {
    flex-wrap: nowrap;
    margin-inline: 0 !important;
  }

  .model-confirmation__swipe-rail.ant-row > .ant-col {
    padding-inline: 0 !important;
  }

  .model-confirmation__swipe-rail-item {
    min-width: min(86vw, 360px);
    flex: 0 0 min(86vw, 360px);
    scroll-snap-align: start;
    scroll-snap-stop: always;
  }

  .model-confirmation__swipe-rail::-webkit-scrollbar { height: 5px; }
  .model-confirmation__swipe-rail::-webkit-scrollbar-track { background: #EEF3F8; border-radius: 999px; }
  .model-confirmation__swipe-rail::-webkit-scrollbar-thumb { background: #9CB0C4; border-radius: 999px; }

  .model-confirmation__model-card.ant-card {
    height: 100%;
    overflow: hidden;
    border: 1px solid #DDE7F1;
    border-radius: 14px;
    background: #FFFFFF;
    box-shadow: 0 6px 18px rgba(15, 39, 66, 0.045);
    transition: transform 160ms ease-out, box-shadow 160ms ease-out, border-color 160ms ease-out;
  }

  .model-confirmation__model-card--primary.ant-card { border-color: #AFCBEC; }
  .model-confirmation__model-card.ant-card:hover {
    transform: translateY(-2px);
    border-color: #91B8E2;
    box-shadow: 0 10px 24px rgba(15, 39, 66, 0.075);
  }

  .model-confirmation__model-card.ant-card .ant-card-body {
    display: flex;
    flex-direction: column;
    gap: 14px;
    height: 100%;
    padding: 16px;
  }

  .model-confirmation__model-head {
    display: grid;
    grid-template-columns: minmax(0, 1fr) 78px;
    align-items: start;
    gap: 12px;
  }

  .model-confirmation__identity { min-width: 0; }
  .model-confirmation__model-rank {
    display: inline-flex;
    align-items: center;
    min-height: 24px;
    border-radius: 5px;
    background: #EAF3FE;
    padding: 3px 8px;
    color: #145DA0;
    font-size: 12px;
    font-weight: 700;
  }

  .model-confirmation__model-title {
    display: -webkit-box;
    overflow: hidden;
    margin-top: 8px;
    color: #071C30;
    font-size: 17px;
    font-weight: 750;
    line-height: 1.28;
    overflow-wrap: anywhere;
    -webkit-box-orient: vertical;
    -webkit-line-clamp: 2;
  }

  .model-confirmation__model-meta {
    overflow: hidden;
    margin-top: 5px;
    color: #6B7F93;
    font-size: 12px;
    line-height: 1.5;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .model-confirmation__score {
    width: 78px;
    border: 1px solid #D8E6F4;
    border-radius: 10px;
    background: #F4F8FC;
    padding: 9px 6px;
    text-align: center;
    font-variant-numeric: tabular-nums;
  }

  .model-confirmation__score-value { color: #0B63CE; font-size: 20px; font-weight: 800; line-height: 1; }
  .model-confirmation__score-total { color: #73869A; font-size: 11px; font-weight: 600; }
  .model-confirmation__score-label { margin-top: 5px; color: #62778C; font-size: 11px; }

  .model-confirmation__population {
    display: flex;
    flex-direction: column;
    gap: 7px;
  }

  .model-confirmation__section-label {
    display: flex;
    align-items: center;
    gap: 6px;
    color: #52697F;
    font-size: 12px;
    font-weight: 650;
  }

  .model-confirmation__tag-row { display: flex; flex-wrap: wrap; gap: 6px; }
  .model-confirmation__tag-row .ant-tag { margin-inline-end: 0; }
  .model-confirmation__patient-match { color: #2E6A4F; font-size: 12px; }

  .model-confirmation__boundaries {
    display: flex;
    flex-direction: column;
    gap: 7px;
    border-block: 1px solid #E7EDF4;
    padding-block: 11px;
  }

  .model-confirmation__boundary-line {
    display: grid;
    grid-template-columns: minmax(0, 1fr) auto;
    align-items: center;
    gap: 8px;
    color: #263F57;
    font-size: 13px;
    line-height: 1.45;
  }

  .model-confirmation__boundary-line > span:first-child { overflow-wrap: anywhere; }
  .model-confirmation__boundary-state { white-space: nowrap; color: #2E6A4F; font-size: 12px; }
  .model-confirmation__boundary-state--missing { color: #A15C00; }

  .model-confirmation__trust-row {
    display: flex;
    flex-wrap: wrap;
    gap: 7px 12px;
  }

  .model-confirmation__trust-item {
    display: inline-flex;
    align-items: center;
    gap: 5px;
    color: #425B72;
    font-size: 12px;
  }

  .model-confirmation__reason {
    color: #5A6F84;
    font-size: 12px;
    line-height: 1.55;
  }

  .model-confirmation__reason strong { color: #2C465E; font-weight: 650; }
  .model-confirmation__warning.ant-alert { padding: 7px 9px; }

  .model-confirmation__footer {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
    margin-top: auto;
    padding-top: 2px;
  }

  .model-confirmation__footer .ant-btn { min-height: 40px; }
  .model-confirmation__empty {
    border: 1px dashed #B8C9DA;
    border-radius: 12px;
    background: #F8FAFC;
    padding: 18px;
    color: #536B82;
    font-size: 14px;
    line-height: 1.6;
  }

  .model-confirmation__excluded-list { display: grid; gap: 12px; }
  .model-confirmation__excluded-card.ant-card { border-color: #E4CDAA; background: #FFFCF7; }
  .model-confirmation__footer-actions {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 12px;
    border: 1px solid #DDE7F1;
    border-radius: 14px;
    background: #FFFFFF;
    padding: 14px 16px;
  }

  .model-evidence__section { padding-block: 4px 18px; }
  .model-evidence__section + .model-evidence__section { border-top: 1px solid #E7EDF4; padding-top: 18px; }
  .model-evidence__title { display: flex; align-items: center; gap: 7px; margin-bottom: 10px; color: #17324D; font-weight: 700; }
  .model-evidence__boundary { display: grid; grid-template-columns: minmax(0, 1fr) auto; gap: 12px; padding: 8px 0; color: #334E68; }
  .model-evidence__boundary + .model-evidence__boundary { border-top: 1px dashed #DDE7F1; }
  .model-evidence__meta { color: #708399; font-size: 12px; line-height: 1.5; }
  .model-evidence__patient { white-space: nowrap; font-variant-numeric: tabular-nums; }

  .model-comparison__name { min-width: 180px; color: #17324D; font-weight: 650; }
  .model-comparison__score { color: #0B63CE; font-weight: 750; font-variant-numeric: tabular-nums; }

  .model-confirmation__model-card :where(button, a):focus-visible,
  .model-confirmation__panel-actions :where(button, a):focus-visible {
    outline: 2px solid #0B63CE;
    outline-offset: 2px;
  }

  @media (max-width: 768px) {
    .model-confirmation { gap: 12px; }
    .model-confirmation__panel { border-radius: 14px; }
    .model-confirmation__panel-head { padding: 16px 14px 0; }
    .model-confirmation__panel-title { font-size: 18px; }
    .model-confirmation__panel-body { padding: 14px; }
    .model-confirmation__mode-row { align-items: stretch; flex-direction: column; }
    .model-confirmation__mode-row .ant-radio-group { display: grid; grid-template-columns: repeat(2, minmax(0, 1fr)); }
    .model-confirmation__mode-row .ant-radio-button-wrapper { overflow: hidden; text-align: center; text-overflow: ellipsis; }
    .model-confirmation__panel-actions { align-self: stretch; justify-content: flex-end; }
    .model-confirmation__model-card.ant-card .ant-card-body { padding: 14px; }
    .model-confirmation__model-title { display: block; overflow: hidden; font-size: 16px; text-overflow: ellipsis; white-space: nowrap; }
    .model-confirmation__model-head { grid-template-columns: minmax(0, 1fr) 74px; }
    .model-confirmation__score { width: 74px; }
    .model-confirmation__footer { display: grid; grid-template-columns: 1fr; }
    .model-confirmation__footer .ant-btn { width: 100%; min-height: 44px; }
    .model-confirmation__footer-actions { flex-direction: column; align-items: stretch; }
    .model-confirmation__footer-actions .ant-btn { min-height: 44px; }
    .model-evidence__boundary { grid-template-columns: 1fr; gap: 4px; }
    .model-evidence__patient { white-space: normal; }
  }

  @media (prefers-reduced-motion: reduce) {
    .model-confirmation__model-card.ant-card { transition: none; }
    .model-confirmation__model-card.ant-card:hover { transform: none; }
  }
`;

const validationEvidenceLabel = (status?: string) => ({
  validated: '临床证据已验证',
  external_validated: '已有外部临床证据',
  pending: '临床证据未开展',
  not_validated: '未形成临床证据',
}[String(status || 'pending')] || '临床证据状态未知');

const technicalQualificationLabel = (status?: string) => ({
  platform_qualified: '技术资格通过',
  clinical_validated: '技术资格通过',
  legacy_accepted: '存量技术认可',
  not_started: '技术资格未开始',
  qualification_running: '技术资格检查中',
  script_failed: '技术资格未通过',
}[String(status || 'not_started')] || '技术资格状态未知');

const releaseStatusLabel = (status?: string) => ({
  active: '已启用',
  published: '已发布',
  inactive: '未启用',
  draft: '草稿',
}[String(status || 'inactive')] || '状态未知');

const buildPublicationLine = (model: ModelMatchResult) => {
  const info = model.publication_info || {};
  const author = String(info.first_author || '').trim();
  const year = String(info.year || '').trim();
  const journal = String(info.journal || '').trim();
  return [author && year ? `${author} (${year})` : author || year, journal]
    .filter(Boolean)
    .join(' · ');
};

const getModelDisplayName = (
  model: {
    model_id?: string;
    model_name?: string;
    display_name?: string;
    publication_info?: { first_author?: string; year?: string | number };
  },
  drugName?: string,
) => getCanonicalModelDisplayName({
  modelId: model.model_id,
  modelName: model.model_name,
  displayName: model.display_name,
  drugName,
  publicationInfo: model.publication_info,
});

const formatNumber = (value: unknown): string => {
  const numeric = Number(value);
  return Number.isFinite(numeric) ? `${numeric}` : String(value ?? '—');
};

const formatBoundaryRule = (rule: BoundaryMatchDetail): string => {
  const label = rule.covariate_label || rule.covariate_key;
  const unit = rule.unit ? ` ${rule.unit}` : '';
  if (rule.rule_type === 'categorical') {
    const values = (rule.allowed_values || []).join('、') || '未配置';
    return `${label} ${rule.operator === 'NOT IN' ? '不属于' : '属于'} ${values}`;
  }
  if (rule.operator === 'between') {
    return `${label} ${formatNumber(rule.min_value)}–${formatNumber(rule.max_value)}${unit}`;
  }
  const threshold = ['<=', '<'].includes(rule.operator) ? rule.max_value : rule.min_value;
  const operator = rule.operator === '==' ? '=' : rule.operator === '!=' ? '≠' : rule.operator;
  return `${label} ${operator}${formatNumber(threshold)}${unit}`;
};

const formatPatientBoundaryValue = (rule: BoundaryMatchDetail): string => {
  if (rule.patient_value == null || rule.patient_value === '') return '患者值缺失';
  return `患者值 ${formatNumber(rule.patient_value)}${rule.unit ? ` ${rule.unit}` : ''}`;
};

const boundaryStatusLabel = (status: BoundaryMatchDetail['status']) => ({
  in_range: '符合',
  out_of_range: '不符合',
  missing: '待补充',
  unknown: '待复核',
}[status]);

const selectKeyBoundaries = (model: ModelMatchResult): BoundaryMatchDetail[] => {
  const rules = [...(model.applicable_boundaries || [])];
  const priorities: Record<string, number> = {
    age_pma: 0,
    pma: 0,
    age_pna: 1,
    pna: 1,
    gestational_age: 2,
    ga: 2,
    age_years: 3,
    age: 3,
    weight: 4,
    wt: 4,
  };
  return rules
    .sort((left, right) => (priorities[left.covariate_key.toLowerCase()] ?? 20) - (priorities[right.covariate_key.toLowerCase()] ?? 20))
    .slice(0, 3);
};

const populationTagsFor = (model: ModelMatchResult): string[] => (
  model.applicable_population_tags?.length
    ? model.applicable_population_tags
    : model.population_tags || []
);

const ModelRecommendation: React.FC<ModelRecommendationProps> = ({
  covariates,
  drugName,
  onSelect,
  onFallback,
  supportsPkFormula = true,
  administrationMode,
  supportedAdministrationModes,
  onAdministrationModeChange,
}) => {
  const [loading, setLoading] = useState(true);
  const [context, setContext] = useState<IntakeContextResponse | null>(null);
  const [error, setError] = useState('');
  const [reloadToken, setReloadToken] = useState(0);
  const [showExcluded, setShowExcluded] = useState(false);
  const [selectingModelId, setSelectingModelId] = useState<string | null>(null);
  const [detailModel, setDetailModel] = useState<ModelMatchResult | null>(null);
  const [comparisonOpen, setComparisonOpen] = useState(false);
  const { isMobileShell } = useResponsive();

  useEffect(() => {
    let alive = true;
    const run = async () => {
      if (!drugName) {
        setContext(null);
        setLoading(false);
        return;
      }
      setLoading(true);
      setError('');
      try {
        const next = await intakeAPI.context({
          drug_id: drugName,
          covariates,
          administration_mode: administrationMode,
        });
        if (alive) setContext(next);
      } catch (err: unknown) {
        if (alive) setError(formatErrorMessage(err, '候选模型上下文加载失败'));
      } finally {
        if (alive) setLoading(false);
      }
    };
    run();
    return () => { alive = false; };
  }, [administrationMode, covariates, drugName, reloadToken]);

  const recommended = useMemo(() => context?.recommended_models || [], [context]);
  const otherModels = useMemo(() => context?.other_evaluated_models || [], [context]);
  const primaryModels = useMemo(
    () => (recommended.length > 0 ? recommended : otherModels),
    [otherModels, recommended],
  );
  const secondaryModels = useMemo(
    () => (recommended.length > 0 ? otherModels : []),
    [otherModels, recommended.length],
  );
  const excludedModels = useMemo(() => context?.excluded_models || [], [context]);
  const comparableModels = useMemo(() => {
    const byId = new Map<string, ModelMatchResult>();
    [...primaryModels, ...secondaryModels].forEach((model) => byId.set(model.model_id, model));
    return Array.from(byId.values());
  }, [primaryModels, secondaryModels]);
  const modelReadinessById = useMemo(
    () => new Map((context?.model_readiness || []).map((item) => [item.model_id, item])),
    [context?.model_readiness],
  );

  const validateAndSelect = async (model: ModelMatchResult): Promise<boolean> => {
    if (!drugName) return false;
    setSelectingModelId(model.model_id);
    try {
      const confirmedContext = await intakeAPI.context({
        drug_id: drugName,
        covariates,
        confirmed_model_id: model.model_id,
        administration_mode: administrationMode,
      });
      const readiness = confirmedContext.model_readiness?.find((item) => item.model_id === model.model_id);
      const missingForModel = readiness?.missing_required_covariates
        || confirmedContext.next_required_covariates.filter(
          (item) => item.requirement_level === 'required_for_selected_model'
            || (!item.requirement_level && item.required && (item.model_ids || []).includes(model.model_id)),
        );
      if (missingForModel.length > 0) {
        message.error(`所选模型还缺少必填协变量：${missingForModel.map((item) => item.label).join('、')}。请返回患者信息补齐后再选择。`);
        return false;
      }
      onSelect(model);
      return true;
    } catch (err: unknown) {
      message.error(formatErrorMessage(err, '模型必填协变量校验失败'));
      return false;
    } finally {
      setSelectingModelId(null);
    }
  };

  const renderModelCard = (model: ModelMatchResult, index: number, tier: ModelTier) => {
    const title = getModelDisplayName(model, drugName);
    const tags = populationTagsFor(model);
    const visibleTags = tags.slice(0, 3);
    const keyBoundaries = selectKeyBoundaries(model);
    const reasons = (model.match_reasons || []).filter(Boolean).slice(0, 2);
    const requirementReadiness = modelReadinessById.get(model.model_id);
    const missingRequirementLabels = requirementReadiness?.missing_required_covariates.map((item) => item.label) || [];

    return (
      <Card className={`model-confirmation__model-card model-confirmation__model-card--${tier}`} bordered={false}>
        <div className="model-confirmation__model-head">
          <div className="model-confirmation__identity">
            <div className="model-confirmation__model-rank">
              {tier === 'primary' ? '优先候选' : '备选'} {String(index + 1).padStart(2, '0')}
            </div>
            <Tooltip title={title} placement="topLeft">
              <div className="model-confirmation__model-title">{title}</div>
            </Tooltip>
            <div className="model-confirmation__model-meta">{buildPublicationLine(model) || model.model_id}</div>
          </div>
          <div className="model-confirmation__score" aria-label={`匹配度 ${model.score} 分，共 100 分`}>
            <div className="model-confirmation__score-value">{Number(model.score).toFixed(1)}</div>
            <div className="model-confirmation__score-total">/100</div>
            <div className="model-confirmation__score-label">匹配度</div>
          </div>
        </div>

        <div className="model-confirmation__population">
          <div className="model-confirmation__section-label"><TeamOutlined />适用人群</div>
          <div className="model-confirmation__tag-row">
            {visibleTags.length ? visibleTags.map((tag) => (
              <Tag key={tag} className="clinical-entity-tag" color={getPopulationTagColor(tag)}>{tag}</Tag>
            )) : <Tag>以配置边界为准</Tag>}
            {tags.length > visibleTags.length ? <Tag>+{tags.length - visibleTags.length}</Tag> : null}
          </div>
          {model.matched_sub_population ? (
            <div className="model-confirmation__patient-match"><AimOutlined /> 当前患者：{model.matched_sub_population}</div>
          ) : null}
        </div>

        <div className="model-confirmation__boundaries">
          <div className="model-confirmation__section-label">
            <AimOutlined />适用边界
            {model.has_compound_boundaries ? <Tag>复合规则</Tag> : null}
          </div>
          {keyBoundaries.length ? keyBoundaries.map((rule, ruleIndex) => (
            <div className="model-confirmation__boundary-line" key={`${rule.group_name}-${rule.covariate_key}-${ruleIndex}`}>
              <span>{formatBoundaryRule(rule)}</span>
              <span className={`model-confirmation__boundary-state${rule.status === 'missing' ? ' model-confirmation__boundary-state--missing' : ''}`}>
                {rule.status === 'in_range' ? <CheckCircleOutlined /> : rule.status === 'missing' ? <InfoCircleOutlined /> : <WarningOutlined />} {boundaryStatusLabel(rule.status)}
              </span>
            </div>
          )) : (
            <div className="model-confirmation__boundary-line">
              <span>{model.age_range !== '未配置' ? `年龄 ${model.age_range}` : `体重 ${model.weight_range}`}</span>
              <span className="model-confirmation__boundary-state">已匹配</span>
            </div>
          )}
          {(model.applicable_boundaries?.length || 0) > keyBoundaries.length ? (
            <Text type="secondary" style={{ fontSize: 12 }}>另有 {(model.applicable_boundaries?.length || 0) - keyBoundaries.length} 项边界，详见依据</Text>
          ) : null}
        </div>

        <div className="model-confirmation__trust-row" aria-label="模型可信状态">
          <Tooltip title="模型脚本、参数和运行契约的技术审查状态">
            <span className="model-confirmation__trust-item"><SafetyCertificateOutlined />{technicalQualificationLabel(model.qualification_status)}</span>
          </Tooltip>
          <Tooltip title="模型临床验证及外部证据状态">
            <span className="model-confirmation__trust-item"><ExperimentOutlined />{validationEvidenceLabel(model.validation_status)}</span>
          </Tooltip>
          <Tooltip title="当前模型发布与启用状态">
            <span className="model-confirmation__trust-item"><CheckCircleOutlined />{releaseStatusLabel(model.model_status)}</span>
          </Tooltip>
        </div>

        {requirementReadiness ? (
          <div>
            <Tooltip title={missingRequirementLabels.length ? missingRequirementLabels.join('、') : '所需协变量已齐'}>
              <Tag color={requirementReadiness.ready ? 'green' : 'gold'}>
                {requirementReadiness.ready ? '必填已齐' : `缺 ${missingRequirementLabels.length} 项`}
              </Tag>
            </Tooltip>
          </div>
        ) : null}

        <div className="model-confirmation__reason">
          <strong>保留原因：</strong>{reasons.length ? reasons.join(' · ') : '患者信息满足当前模型的有效配置边界。'}
        </div>

        {model.warnings?.length ? (
          <Alert className="model-confirmation__warning" type="warning" showIcon message={model.warnings.join('；')} />
        ) : null}

        <div className="model-confirmation__footer">
          <Button icon={<InfoCircleOutlined />} onClick={() => setDetailModel(model)}>查看依据</Button>
          <Button
            type={requirementReadiness?.ready === false ? 'default' : 'primary'}
            icon={<CheckCircleOutlined />}
            loading={selectingModelId === model.model_id}
            onClick={() => void validateAndSelect(model)}
          >
            {requirementReadiness?.ready === false ? '补齐后选择' : '选择模型'}
          </Button>
        </div>
      </Card>
    );
  };

  const renderModelGrid = (models: ModelMatchResult[], tier: ModelTier) => {
    const useSwipe = isMobileShell && models.length > 1;
    return (
      <Row
        gutter={useSwipe ? [0, 0] : [14, 14]}
        className={`model-confirmation__card-grid${useSwipe ? ' model-confirmation__swipe-rail' : ''}`}
        role={useSwipe ? 'region' : undefined}
        aria-label={useSwipe ? `${tier === 'primary' ? '候选' : '备选'}模型横向列表` : undefined}
        tabIndex={useSwipe ? 0 : undefined}
      >
        {models.map((model, index) => (
          <Col
            xs={24}
            lg={12}
            xl={8}
            key={model.model_id}
            className={useSwipe ? 'model-confirmation__swipe-rail-item' : undefined}
          >
            {renderModelCard(model, index, tier)}
          </Col>
        ))}
      </Row>
    );
  };

  const comparisonColumns = [
    {
      title: '模型',
      key: 'model',
      fixed: 'left' as const,
      width: 210,
      render: (_: unknown, model: ModelMatchResult) => <div className="model-comparison__name">{getModelDisplayName(model, drugName)}</div>,
    },
    {
      title: '匹配度',
      dataIndex: 'score',
      width: 90,
      render: (score: number) => <span className="model-comparison__score">{Number(score).toFixed(1)}</span>,
    },
    {
      title: '适用人群',
      width: 190,
      render: (_: unknown, model: ModelMatchResult) => populationTagsFor(model).join('、') || '以配置边界为准',
    },
    {
      title: '关键边界',
      width: 260,
      render: (_: unknown, model: ModelMatchResult) => selectKeyBoundaries(model).map(formatBoundaryRule).join('；') || '详见模型边界',
    },
    {
      title: '技术资格',
      width: 150,
      render: (_: unknown, model: ModelMatchResult) => technicalQualificationLabel(model.qualification_status),
    },
    {
      title: '临床证据',
      width: 160,
      render: (_: unknown, model: ModelMatchResult) => validationEvidenceLabel(model.validation_status),
    },
    {
      title: '警告',
      width: 220,
      render: (_: unknown, model: ModelMatchResult) => model.warnings?.join('；') || '无',
    },
  ];

  if (loading) {
    return (
      <div className="model-confirmation">
        <style>{STYLES}</style>
        <section className="model-confirmation__panel">
          <div className="model-confirmation__panel-head"><div><div className="model-confirmation__panel-title">正在加载候选模型…</div></div></div>
          <div className="model-confirmation__panel-body"><Row gutter={[14, 14]}>{[0, 1].map((item) => <Col xs={24} lg={12} key={item}><Card><Skeleton active /></Card></Col>)}</Row></div>
        </section>
      </div>
    );
  }

  if (error) {
    return <Alert type="error" showIcon message="候选模型加载失败" description={error} action={<Button onClick={() => setReloadToken((value) => value + 1)}>重新加载</Button>} />;
  }

  if (!context) return null;

  return (
    <div className="model-confirmation">
      <style>{STYLES}</style>

      <section className="model-confirmation__panel">
        <div className="model-confirmation__panel-head">
          <div>
            <div className="model-confirmation__panel-title">{recommended.length > 0 ? '候选模型' : '当前可选模型'}</div>
            <div className="model-confirmation__panel-desc">按适用人群、患者边界、模型来源和可信状态综合排序。</div>
          </div>
          <div className="model-confirmation__panel-actions">
            {comparableModels.length > 1 ? <Button onClick={() => setComparisonOpen(true)}>对比模型</Button> : null}
          </div>
        </div>
        <div className="model-confirmation__panel-body">
          {supportedAdministrationModes.length > 1 ? (
            <div className="model-confirmation__mode-row">
              <span className="model-confirmation__mode-label">给药方式</span>
              <Radio.Group
                value={administrationMode}
                onChange={(event) => onAdministrationModeChange(event.target.value)}
              >
                {supportedAdministrationModes.map((mode) => (
                  <Radio.Button key={mode} value={mode}>
                    {getAdministrationModeLabel(mode)}
                  </Radio.Button>
                ))}
              </Radio.Group>
            </div>
          ) : null}
          {primaryModels.length ? renderModelGrid(primaryModels, 'primary') : (
            <div className="model-confirmation__empty">
              当前没有模型通过有效边界。请补充关键协变量或检查后台模型适用范围。
              {supportsPkFormula ? <div style={{ marginTop: 12 }}><Button onClick={onFallback}>改用 PK 公式法</Button></div> : null}
            </div>
          )}
        </div>
      </section>

      {secondaryModels.length > 0 ? (
        <section className="model-confirmation__panel">
          <div className="model-confirmation__panel-head">
            <div>
              <div className="model-confirmation__panel-title">其他备选</div>
              <div className="model-confirmation__panel-desc">这些模型通过当前边界，但综合优先级较低，可用于人工比较。</div>
            </div>
          </div>
          <div className="model-confirmation__panel-body">{renderModelGrid(secondaryModels, 'secondary')}</div>
        </section>
      ) : null}

      {excludedModels.length > 0 ? (
        <section className="model-confirmation__panel">
          <div className="model-confirmation__panel-head">
            <div>
              <div className="model-confirmation__panel-title">高风险排除模型</div>
              <div className="model-confirmation__panel-desc">默认不参与主决策，仅在临床专家明确认知风险时人工启用。</div>
            </div>
            <Button icon={<EyeOutlined />} onClick={() => setShowExcluded((value) => !value)}>
              {showExcluded ? '收起' : `查看 (${excludedModels.length})`}
            </Button>
          </div>
          {showExcluded ? (
            <div className="model-confirmation__panel-body">
              <div className={`model-confirmation__excluded-list${isMobileShell && excludedModels.length > 1 ? ' model-confirmation__swipe-rail' : ''}`}>
                {excludedModels.map((item) => (
                  <Card key={item.model_id} className="model-confirmation__excluded-card" size="small">
                    <Space direction="vertical" size={10} style={{ width: '100%' }}>
                      <Space wrap><Tag color="gold">需专家复核</Tag><Text strong>{getModelDisplayName(item)}</Text></Space>
                      <Text type="secondary">排除原因：{item.reason}</Text>
                      <Button danger disabled>人工选择该模型（已阻止）</Button>
                      <Text type="secondary" style={{ fontSize: 12 }}>
                        当前患者不满足该模型边界，后端不会允许绕过边界进入计算；如边界配置有误，请先修正后台配置。
                      </Text>
                    </Space>
                  </Card>
                ))}
              </div>
            </div>
          ) : null}
        </section>
      ) : null}

      {supportsPkFormula && primaryModels.length > 0 ? (
        <div className="model-confirmation__footer-actions">
          <Text type="secondary">如当前病例不适合 PopPK 模型路径，可切换到 PK 公式法。</Text>
          <Button onClick={onFallback}>改用 PK 公式法</Button>
        </div>
      ) : null}

      <Drawer
        title={detailModel ? getModelDisplayName(detailModel, drugName) : '模型依据'}
        open={Boolean(detailModel)}
        onClose={() => setDetailModel(null)}
        placement={isMobileShell ? 'bottom' : 'right'}
        width={isMobileShell ? undefined : 560}
        height={isMobileShell ? '84vh' : undefined}
      >
        {detailModel ? (
          <>
            <section className="model-evidence__section">
              <div className="model-evidence__title"><BookOutlined />来源与研究人群</div>
              <Paragraph>{buildPublicationLine(detailModel) || '暂无结构化文献来源'}</Paragraph>
              <Paragraph type="secondary">研究人群：{detailModel.population && detailModel.population !== 'Unknown' ? detailModel.population : '暂无研究人群描述'}</Paragraph>
              {detailModel.publication_info?.pmid ? <Text type="secondary">PMID：{detailModel.publication_info.pmid}</Text> : null}
            </section>

            <section className="model-evidence__section">
              <div className="model-evidence__title"><TeamOutlined />有效适用人群</div>
              <div className="model-confirmation__tag-row">
                {populationTagsFor(detailModel).length ? populationTagsFor(detailModel).map((tag) => (
                  <Tag key={tag} className="clinical-entity-tag" color={getPopulationTagColor(tag)}>{tag}</Tag>
                )) : <Tag>以配置边界为准</Tag>}
              </div>
              {detailModel.matched_sub_population ? <Paragraph style={{ marginTop: 10 }}>当前患者匹配：{detailModel.matched_sub_population}</Paragraph> : null}
            </section>

            <section className="model-evidence__section">
              <div className="model-evidence__title"><AimOutlined />适用边界与患者值</div>
              {detailModel.applicable_boundaries?.length ? detailModel.applicable_boundaries.map((rule, index) => (
                <div className="model-evidence__boundary" key={`${rule.group_name}-${rule.covariate_key}-${index}`}>
                  <div>
                    <div>{formatBoundaryRule(rule)}</div>
                    <div className="model-evidence__meta">{rule.group_name} · {rule.group_logic}{rule.group_negated ? ' · NOT' : ''}</div>
                  </div>
                  <div className="model-evidence__patient">{formatPatientBoundaryValue(rule)} · {boundaryStatusLabel(rule.status)}</div>
                </div>
              )) : <Text type="secondary">暂无结构化边界详情</Text>}
            </section>

            <section className="model-evidence__section">
              <div className="model-evidence__title"><SafetyCertificateOutlined />可信状态</div>
              <Space direction="vertical" size={8}>
                <Text>技术资格：{technicalQualificationLabel(detailModel.qualification_status)}</Text>
                <Text>临床证据：{validationEvidenceLabel(detailModel.validation_status)}</Text>
                <Text>发布状态：{releaseStatusLabel(detailModel.model_status)}</Text>
              </Space>
            </section>

            <section className="model-evidence__section">
              <div className="model-evidence__title"><InfoCircleOutlined />保留原因与警告</div>
              {(detailModel.match_reasons || []).length ? (
                <ul>{detailModel.match_reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul>
              ) : <Paragraph type="secondary">暂无额外结构化说明</Paragraph>}
              {detailModel.warnings?.length ? <Alert type="warning" showIcon message={detailModel.warnings.join('；')} /> : null}
            </section>
          </>
        ) : null}
      </Drawer>

      <Drawer
        title="候选模型对比"
        open={comparisonOpen}
        onClose={() => setComparisonOpen(false)}
        placement={isMobileShell ? 'bottom' : 'right'}
        width={isMobileShell ? undefined : '86vw'}
        height={isMobileShell ? '88vh' : undefined}
      >
        <Table
          rowKey="model_id"
          size="small"
          columns={comparisonColumns}
          dataSource={comparableModels}
          pagination={false}
          scroll={{ x: 1300 }}
        />
      </Drawer>
    </div>
  );
};

export default ModelRecommendation;
